// Human-readable RU labels for backend tools rendered by DefaultToolCard.
//
// Custom renderers (SkillCard, CanvasCard, PythonRunCard, ...) own their own
// labels because they format more than a title — this module covers only
// the tools that fall through to DefaultToolCard.
//
// Resolution order:
//   1. Static formatter in LABELS (system tools, can use input args).
//   2. Curated MCP-tool formatter (PLAYWRIGHT_LABELS) — deterministic RU
//      labels that work in history too, where no connector is running.
//   3. MCP `Tool.title` advertised by the server (live sessions only).
//   4. Humanized snake_case fallback — readable, never a raw tool id.

type Formatter = (input: Record<string, unknown>) => string;

const str = (v: unknown): string => (v == null ? '' : String(v)).trim();

const LABELS: Record<string, Formatter> = {
  webfetch: (input) => `Загрузка ${str(input.url)}`.trim(),
  file_list: (input) => (input.path ? `Список файлов: ${str(input.path)}` : 'Список файлов'),
  file_read: (input) => `Чтение ${str(input.path) || 'файла'}`,
  file_write: (input) => `Запись ${str(input.path) || 'файла'}`,
  file_delete: (input) => `Удаление ${str(input.path) || 'файла'}`,
  url_to_workspace: (input) => `Скачивание ${str(input.path) || 'файла'}`,
  workspace_to_url: (input) => `Выгрузка ${str(input.path) || 'файла'}`,
  render_slides: (input) =>
    str(input.path) ? `Рендер слайдов: ${str(input.path)}` : 'Рендер слайдов',
};

// Playwright MCP browser tools. Keyed by the unprefixed name (`browser_click`),
// so the same map works regardless of the connector prefix (`playwright_…`).
// Where the model passes a human-readable `element` description, we surface it.
const PLAYWRIGHT_LABELS: Record<string, Formatter> = {
  browser_navigate: (i) => (i.url ? `Открываю ${str(i.url)}` : 'Открываю страницу'),
  browser_navigate_back: () => 'Возврат на предыдущую страницу',
  browser_click: (i) => (i.element ? `Клик: ${str(i.element)}` : 'Клик'),
  browser_type: (i) => (i.element ? `Ввод текста: ${str(i.element)}` : 'Ввод текста'),
  browser_fill_form: () => 'Заполнение формы',
  browser_select_option: (i) => (i.element ? `Выбор: ${str(i.element)}` : 'Выбор из списка'),
  browser_press_key: (i) => (i.key ? `Нажатие клавиши ${str(i.key)}` : 'Нажатие клавиши'),
  browser_hover: (i) => (i.element ? `Наведение: ${str(i.element)}` : 'Наведение на элемент'),
  browser_drag: () => 'Перетаскивание',
  browser_snapshot: () => 'Снимок страницы',
  browser_take_screenshot: () => 'Скриншот страницы',
  browser_wait_for: () => 'Ожидание',
  browser_file_upload: () => 'Загрузка файла',
  browser_handle_dialog: () => 'Диалоговое окно',
  browser_evaluate: () => 'Выполнение скрипта на странице',
  browser_console_messages: () => 'Сообщения консоли',
  browser_network_requests: () => 'Сетевые запросы',
  browser_resize: () => 'Изменение размера окна',
  browser_close: () => 'Закрытие браузера',
  browser_tab_list: () => 'Список вкладок',
  browser_tab_new: () => 'Новая вкладка',
  browser_tab_select: () => 'Переключение вкладки',
  browser_tab_close: () => 'Закрытие вкладки',
  browser_pdf_save: () => 'Сохранение в PDF',
  browser_install: () => 'Установка браузера',
};

// Strip the connector prefix from an MCP tool id and look it up in `map`.
// `playwright_browser_click` and a bare `browser_click` both resolve to the
// `browser_*` entry.
function lookupBrowser(tool: string): Formatter | undefined {
  const i = tool.indexOf('browser_');
  if (i === -1) return undefined;
  return PLAYWRIGHT_LABELS[tool.slice(i)];
}

// Last resort: turn `post-copy_send_message` into `Post copy send message`
// instead of showing a raw tool id.
function humanize(tool: string): string {
  const words = tool.replace(/[_-]+/g, ' ').trim();
  if (!words) return tool;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function labelForTool(
  tool: string,
  input: Record<string, unknown> | undefined,
  mcpTitle?: string | null,
): string {
  const args = input ?? {};
  const fn = LABELS[tool] ?? lookupBrowser(tool);
  if (fn) return fn(args);
  if (mcpTitle) return mcpTitle;
  return humanize(tool);
}
